import type { BatteryArbitragePresentationDto } from "~/api/dto/battery-arbitrage.dto"
import batteryHero from "~/assets/photo/battery-only-proposal/battery-hero.jpg"

import { arbitrageBasisQualifier } from "../arbitrage-assumptions"
import type { BatteryPresentationProposal } from "../presentation-proposal"
import { Eyebrow, GradText, Icon } from "../primitives"
import { useDesktopViewport } from "../use-desktop-viewport"
import { formatCreatedDate, formatUsd } from "../view-helpers"

export function Hero({
  proposal,
  arbitrage,
}: {
  proposal: BatteryPresentationProposal
  arbitrage: BatteryArbitragePresentationDto | null
}): JSX.Element {
  const isDesktop = useDesktopViewport()
  const created = formatCreatedDate(proposal.createdAt)
  const manufacturer = proposal.equipment?.manufacturer?.trim() || `—`
  const model = proposal.equipment?.name?.trim() || `—`
  const usable =
    proposal.equipment?.usableKwh != null
      ? `${proposal.equipment.usableKwh} kWh`
      : `—`
  const units =
    proposal.equipment?.quantity != null
      ? String(proposal.equipment.quantity)
      : `—`

  // A saved zero or negative first-year result is a result: the stat renders
  // its own signed figure rather than being dropped as if nothing had been
  // calculated. Only the absence of a current result hides it.
  const stats = [
    {
      icon: `panel` as const,
      label: `Manufacturer`,
      value: manufacturer,
      sub: model,
    },
    {
      icon: `battery` as const,
      label: `Storage size`,
      value: usable,
      sub: `Usable capacity`,
    },
    {
      icon: `grid` as const,
      label: `Number of batteries`,
      value: units,
      sub: `Units`,
    },
    ...(arbitrage
      ? [
          {
            icon: `bolt` as const,
            label: `Year 1 arbitrage savings`,
            value: formatUsd(arbitrage.annualSavingsUsd),
            sub: arbitrageBasisQualifier(arbitrage),
          },
        ]
      : []),
  ]

  return (
    <section
      id="hero"
      className="relative scroll-mt-24 overflow-hidden px-6 pt-14 pb-20 lg:px-16 lg:pt-[70px] lg:pb-[90px]"
    >
      <img
        src={batteryHero}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 size-full object-cover object-right opacity-90"
      />
      <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-hero-legibility)]" />
      <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-hero-edge-fade)]" />
      <div className="pointer-events-none absolute inset-0 bg-[image:var(--bp-hero-glow)]" />
      {/* The date shares the eyebrow's line on desktop and wraps above the
       * copy on narrow screens, where the two would otherwise collide. */}
      <div className="relative flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="order-2 max-w-[760px] lg:order-1">
          <Eyebrow className="mb-[18px] text-bp-text-soft">
            Energy resilience
            {manufacturer !== `—` ? ` · Powered by ${manufacturer}` : ``}
          </Eyebrow>
          <h1 className="font-display max-w-[640px] text-[length:var(--step-5)] leading-[0.98] font-semibold tracking-[-0.04em] text-bp-text lg:text-[76px]">
            When the grid goes down,
            <br />
            <GradText>your life doesn&apos;t</GradText> have to.
          </h1>
          <p className="mt-5 max-w-[540px] text-[length:var(--step-0)] leading-relaxed text-bp-text-soft">
            A home battery keeps the lights on through outages — and quietly
            earns its keep every day by shifting your energy off peak utility
            rates.
          </p>
        </div>
        {created ? (
          <Eyebrow className="order-1 basis-full text-bp-text-dim lg:order-2 lg:basis-auto">
            {created}
          </Eyebrow>
        ) : null}
      </div>
      {isDesktop ? (
        <div
          className={`relative mt-10 grid gap-3.5 sm:grid-cols-2 ${arbitrage ? `xl:grid-cols-4` : `xl:grid-cols-3`}`}
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="relative flex min-h-[168px] flex-col gap-1.5 rounded-lg border border-bp-border-strong bg-bp-surface/90 p-6 shadow-[0_10px_34px_rgba(0,0,0,0.4)] backdrop-blur-lg"
            >
              <div className="absolute top-5 right-5 grid size-8 place-items-center rounded-md bg-bp-glass-hi text-bp-accent">
                <Icon name={stat.icon} className="size-3.5" />
              </div>
              <Eyebrow className="pr-10">{stat.label}</Eyebrow>
              <div className="font-display mt-1 text-[length:var(--step-2)] font-semibold tracking-[-0.025em] tabular-nums text-bp-text">
                {stat.value}
              </div>
              <div className="mt-auto text-[length:var(--step--2)] text-bp-text-muted">
                {stat.sub}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="relative mt-9 rounded-lg border border-bp-border-strong bg-bp-surface/90 px-5 py-1.5 shadow-[0_10px_34px_rgba(0,0,0,0.4)] backdrop-blur-lg">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`flex items-baseline justify-between gap-4 py-3 ${
                index < stats.length - 1 ? `border-b border-bp-border` : ``
              }`}
            >
              <span className="text-[length:var(--step--1)] text-bp-text-muted">
                {stat.label}
              </span>
              <span className="text-right">
                <span className="font-display block text-[length:var(--step-0)] font-semibold tracking-[-0.02em] tabular-nums text-bp-text">
                  {stat.value}
                </span>
                <span className="block text-[length:var(--step--3)] text-bp-text-dim">
                  {stat.sub}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
